import { useRef, useState } from "react"
import {
	IconMonitorOutline18 as MonitorSmartphoneIcon,
	IconMoonOutline18 as MoonIcon,
	IconSunOutline18 as SunIcon,
	IconTrashOutline18 as TrashIcon,
	IconArrowUpFromLineOutline18 as UploadIcon,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import { cn } from "@productlane/lib/src/browser"
import { FormInput, Input, Tooltip } from "@productlane/ui"

import { ColorSwatchPicker } from "@/components/color-swatch-picker"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { parseBackgroundColor } from "./background-color"

// Shared by the workspace Theme page and a named portal's own theme.

export const THEME_COLOR_FIELDS = [
	"accentColor",
	"buttonTextColor",
	"darkModeAccentColor",
	"darkModeButtonTextColor",
] as const

export type ThemeColorField = (typeof THEME_COLOR_FIELDS)[number]
export type ThemeColors = Record<ThemeColorField, string>

export function EmbedImageField({
	imageUrl,
	onUploaded,
	onCleared,
}: {
	imageUrl: string | null
	onUploaded: (fileUrl: string) => Promise<void>
	onCleared: () => Promise<void>
}) {
	const { role } = useRole()
	const getSignedUrlMutation = api.file.getUrl.useMutation()

	const imageInput = useRef<HTMLInputElement | null>(null)
	const imageField = useRef<HTMLImageElement | null>(null)
	const pendingFile = useRef<File | null>(null)
	const [imagePreview, setImagePreview] = useState<string | null>(null)

	const openFilePicker = () => imageInput.current?.click()

	const uploadEmbedImage = async (file: File) => {
		if (!imageField.current) return
		const height = imageField.current.naturalHeight
		const width = imageField.current.naturalWidth
		if (width !== 1200 || height !== 630) {
			return toast.error({
				title: "The image dimensions are incorrect",
				description: "Please make sure the image is 1200x630 pixels",
			})
		}

		const { fileUrl, uploadUrl } = await getSignedUrlMutation.mutateAsync({
			fileName: file.name,
			fileType: file.type,
		})
		const res = await fetch(uploadUrl, {
			method: "PUT",
			body: file,
			headers: { "Content-Type": file.type },
		})
		if (!res.ok) throw new Error("File upload failed")
		analytics.track("upload-custom-portal-metadata-image")

		await onUploaded(fileUrl)
	}

	const shownUrl = imagePreview ?? imageUrl

	return (
		<>
			<div>
				<p className="text-sm font-medium">Embed image</p>
				<p className="text-xs text-secondary">
					Set an image that will be used when you embed your portal. Such as
					when sharing a link on socials. The image dimensions should be
					1200x630 pixels.
				</p>
			</div>
			<div className="w-fit">
				<div className="rounded border border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
					{shownUrl ? (
						<div className="relative">
							<img
								ref={imageField}
								alt="workspace logo"
								height={230}
								width={441}
								src={shownUrl}
								onLoad={() => {
									const file = pendingFile.current
									if (imagePreview && file) {
										pendingFile.current = null
										void uploadEmbedImage(file)
									}
								}}
								className="max-h-[230px] max-w-[441px] rounded-lg object-contain" // Gotta define max height and width, or it will be buggy when the image is initially rendered
							/>
							<RoleCheck not={["VIEWER"]}>
								<div className="absolute right-4 top-4 flex gap-2">
									<button
										className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900/20 shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black/20 dark:hover:bg-black/30"
										type="button"
										onClick={openFilePicker}
										title="Upload new image"
									>
										<UploadIcon className="h-5 w-5 text-white drop-shadow-sm" />
									</button>
									<button
										className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900/20 shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black/20 dark:hover:bg-black/30"
										type="button"
										onClick={async () => {
											if (role === "VIEWER") {
												return
											}
											setImagePreview(null)
											pendingFile.current = null
											await onCleared()
											toast.success({ title: "Deleted embed image" })
										}}
										title="Delete embed image"
									>
										<TrashIcon className="h-5 w-5 text-white drop-shadow-sm" />
									</button>
								</div>
							</RoleCheck>
						</div>
					) : (
						<button
							type="button"
							className="flex h-24 w-24 items-center justify-center"
							onClick={openFilePicker}
						>
							<UploadIcon className="h-5 w-5" />
						</button>
					)}
				</div>
			</div>
			<input
				name="file"
				type="file"
				ref={imageInput}
				className="hidden"
				onChange={(e) => {
					if (e.target.files?.[0]) {
						setImagePreview(URL.createObjectURL(e.target.files[0]))
						pendingFile.current = e.target.files[0]
					}
					e.target.value = ""
				}}
			/>
		</>
	)
}

const DARK_MODE_OPTIONS = [
	{
		value: "both",
		label: "System – Follows the user's system preference",
		Icon: MonitorSmartphoneIcon,
	},
	{
		value: "false",
		label: "Light mode only",
		Icon: SunIcon,
	},
	{
		value: "true",
		label: "Dark mode only",
		Icon: MoonIcon,
	},
] as const

export function DarkModeField({
	value,
	onChange,
}: {
	value: boolean | null
	onChange: (next: boolean | null) => void
}) {
	const themeValue = value === null ? "both" : String(value)

	return (
		<div className="flex flex-wrap items-center justify-between gap-2">
			<div>
				<div className="text-sm font-medium">Dark mode</div>
				<p className="text-xs text-secondary">
					Choose if you want to use dark mode, light mode, or both on your
					portal.
				</p>
			</div>
			<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
				{DARK_MODE_OPTIONS.map((option) => (
					<Tooltip key={option.value} label={option.label}>
						<button
							type="button"
							className={cn(
								"flex items-center justify-center rounded-[0.45rem] p-1.5 transition-colors",
								themeValue === option.value
									? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
									: "text-secondary hover:text-primary",
							)}
							onClick={() =>
								onChange(
									option.value === "both"
										? null
										: option.value === "true"
											? true
											: false,
								)
							}
						>
							<div className="flex h-7 w-7 items-center justify-center">
								<option.Icon className="h-4 w-4" />
							</div>
						</button>
					</Tooltip>
				))}
			</div>
		</div>
	)
}

/** Needs a surrounding Form whose schema has the four color fields. */
export function ThemeColorFields({
	colors,
	onChange,
	onCommit,
	backgrounds,
	onBackgroundSave,
}: {
	colors: ThemeColors
	onChange: (field: ThemeColorField, hex: string) => void
	onCommit: (field: ThemeColorField) => void
	backgrounds: BackgroundColors
	onBackgroundSave: (field: BackgroundColorField, value: string | null) => void
}) {
	const input = (label: string, field: ThemeColorField) => (
		<ColorInput
			label={label}
			name={field}
			color={colors[field]}
			onChange={(hex) => onChange(field, hex)}
			onBlur={() => onCommit(field)}
		/>
	)
	const background = (mode: string, field: BackgroundColorField) => (
		<BackgroundColorInput
			mode={mode}
			saved={backgrounds[field]}
			fallback={DEFAULT_BACKGROUNDS[field]}
			onSave={(value) => onBackgroundSave(field, value)}
		/>
	)

	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<div>
				<div className="mb-2 flex items-center gap-2">
					<div className="text-sm font-medium">Light mode</div>
					<SunIcon className="h-4 w-4 text-secondary" />
				</div>
				<div className="grid grid-cols-1 gap-4">
					{input("Accent color", "accentColor")}
					{input("Button text color", "buttonTextColor")}
					{background("Light mode", "portalBackgroundColor")}
				</div>
			</div>

			<div>
				<div className="mb-2 flex items-center gap-2">
					<div className="text-sm font-medium">Dark mode</div>
					<MoonIcon className="h-4 w-4 text-secondary" />
				</div>
				<div className="grid grid-cols-1 gap-4">
					{input("Accent color", "darkModeAccentColor")}
					{input("Button text color", "darkModeButtonTextColor")}
					{background("Dark mode", "portalDarkModeBackgroundColor")}
				</div>
			</div>
		</div>
	)
}

/** Saves each color 700ms after the last change the user made to it. */
export function useThemeColorAutosave({
	colors,
	isDirty,
	persist,
}: {
	colors: ThemeColors
	isDirty: (field: ThemeColorField) => boolean
	persist: (field: ThemeColorField, value: string) => void
}) {
	const save = (field: ThemeColorField) => {
		if (isDirty(field)) persist(field, colors[field])
	}
	useDebounce(() => save("accentColor"), 700, [colors.accentColor])
	useDebounce(() => save("buttonTextColor"), 700, [colors.buttonTextColor])
	useDebounce(() => save("darkModeAccentColor"), 700, [
		colors.darkModeAccentColor,
	])
	useDebounce(() => save("darkModeButtonTextColor"), 700, [
		colors.darkModeButtonTextColor,
	])
}

interface ColorInputProps {
	label: string
	name: string
	color: string
	onChange: (hex: string) => void
	onBlur?: () => void
}

function ColorInput({ label, name, color, onChange, onBlur }: ColorInputProps) {
	return (
		<div>
			<div className="text-xs font-medium text-secondary">{label}</div>
			<div className="mt-1 flex gap-2">
				<ColorSwatchPicker
					value={color}
					onChange={onChange}
					onCommit={onBlur}
				/>
				<FormInput name={name} className="flex-1" onBlur={onBlur} />
			</div>
		</div>
	)
}

export type BackgroundColorField =
	| "portalBackgroundColor"
	| "portalDarkModeBackgroundColor"
export type BackgroundColors = Record<BackgroundColorField, string | null>

const DEFAULT_BACKGROUNDS: Record<BackgroundColorField, string> = {
	portalBackgroundColor: "#FFFFFF",
	portalDarkModeBackgroundColor: "#050506",
}

function BackgroundColorInput({
	mode,
	saved,
	fallback,
	onSave,
}: {
	mode: string
	saved: string | null
	fallback: string
	onSave: (value: string | null) => void
}) {
	const { role } = useRole()
	const [draft, setDraft] = useState(saved ?? "")
	const parsed = parseBackgroundColor(draft)
	const commit = () => {
		if (parsed === undefined || parsed === saved) return
		onSave(parsed)
	}
	useDebounce(commit, 700, [draft])

	return (
		<div>
			<div className="text-xs font-medium text-secondary">Background color</div>
			<div className="mt-1 flex items-start gap-2">
				<ColorSwatchPicker
					value={parsed ?? fallback}
					onChange={setDraft}
					onCommit={commit}
					disabled={role === "VIEWER"}
				/>
				<Input
					name={`${mode}-background`}
					aria-label={`${mode} background color`}
					className="flex-1"
					value={draft}
					placeholder={`Default (${fallback})`}
					disabled={role === "VIEWER"}
					onChange={(e) => setDraft(e.target.value)}
					onBlur={commit}
					errorMessage={
						parsed === undefined ? "Use a hex color like #F5F5F0." : undefined
					}
				/>
			</div>
		</div>
	)
}
